import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { importTasks, getStorageStats } from '../../services/firebaseStorage';
import { getUserProfile } from '../../services/userService';
import { useCalendars } from '../../contexts/CalendarContext';
import CalendarSelector from '../Calendar/CalendarSelector';
import CalendarManager from '../Calendar/CalendarManager';
import NotificationBell from '../Common/NotificationBell';
import Login from '../Auth/Login';
import Register from '../Auth/Register';
import pkg from '../../../package.json';
const { version } = pkg;


const Header = () => {
    const [showMenu, setShowMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authView, setAuthView] = useState('login'); // 'login' or 'register'
    const [showCalendarManager, setShowCalendarManager] = useState(false);
    const [userTier, setUserTier] = useState('free');
    const { currentUser, logout } = useAuth();
    const { toggleTheme, isDark } = useTheme();
    const { selectedCalendar } = useCalendars();

    // Fetch user tier when user is logged in
    useEffect(() => {
        const fetchUserTier = async () => {
            if (currentUser) {
                try {
                    const profile = await getUserProfile(currentUser.uid);
                    if (profile && profile.userTier) {
                        setUserTier(profile.userTier);
                    }
                } catch (error) {
                    console.error('Error fetching user tier:', error);
                }
            }
        };
        fetchUserTier();
    }, [currentUser]);

    // const handleExport = async () => {
    //     if (!currentUser) return;

    //     try {
    //         await exportTasks(currentUser.uid);
    //         setShowMenu(false);
    //     } catch (error) {
    //         alert('Error exporting tasks. Please try again.');
    //     }
    // };

    const handleDownloadTemplate = () => {
        const templateTasks = [
            {
                task: "Example Task 1",
                owner: "John Doe",
                dueDate: "2025-12-01",
                acceptanceCriteria: "Task is completed when all requirements are met",
                dependencies: "None",
                contingencyPlan: "Alternative approach if needed",
                priority: "high",
                status: "not-started",
                completed: false,
                completedAt: null
            },
            {
                task: "Example Task 2",
                owner: "Jane Smith",
                dueDate: "2025-12-15",
                acceptanceCriteria: "Deliverable meets quality standards",
                dependencies: "Example Task 1",
                contingencyPlan: "Backup plan ready",
                priority: "medium",
                status: "in-progress",
                completed: false,
                completedAt: null
            }
        ];

        const dataStr = JSON.stringify(templateTasks, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'tasks-import-template.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setShowMenu(false);
    };

    const handleImport = () => {
        if (!currentUser || !selectedCalendar) {
            alert('Please select a calendar first.');
            return;
        }

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = async (event) => {
                    try {
                        await importTasks(event.target.result, currentUser.uid, selectedCalendar.id);
                        alert('Tasks imported successfully!');
                        window.location.reload();
                    } catch (error) {
                        const errorMessage = error.message || 'Unknown error occurred';
                        alert(`Error importing tasks:\n\n${errorMessage}`);
                    }
                };
                reader.readAsText(file);
            }
        };
        input.click();
        setShowMenu(false);
    };

    const handleViewStats = async () => {
        if (!currentUser) return;

        try {
            const stats = await getStorageStats(currentUser.uid);
            alert(`Storage Statistics:\n\nTotal Tasks: ${stats.totalTasks}\nStorage Used: ${stats.storageSizeKB} KB`);
            setShowMenu(false);
        } catch (error) {
            alert('Error fetching statistics. Please try again.');
        }
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you want to sign out?')) {
            try {
                await logout();
                setShowMenu(false);
            } catch (error) {
                alert('Error signing out. Please try again.');
            }
        }
    };

    return (
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-200">
            <div className="container mx-auto px-4 py-3 md:py-4 max-w-7xl">
                <div className="flex items-center justify-between">
                    {/* Logo and Title */}
                    <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                        <div className="min-w-0 flex-1">
                            <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 dark:from-primary-400 dark:to-purple-400 bg-clip-text text-transparent truncate">
                                Kazeniq Cadence
                            </h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Project & Task Management</p>
                        </div>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-3">
                        {/* Calendar Selector (when logged in) */}
                        {currentUser && (
                            <CalendarSelector onManageClick={() => setShowCalendarManager(true)} />
                        )}

                        {/* Notification Bell (when logged in) */}
                        {currentUser && <NotificationBell />}

                        {/* Dark Mode Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            aria-label="Toggle dark mode"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? (
                                <svg className="w-5 h-5 flex-shrink-0 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5 flex-shrink-0 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                                </svg>
                            )}
                        </button>

                        {/* Auth Buttons (when not logged in) */}
                        {!currentUser && (
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => {
                                        setAuthView('login');
                                        setShowAuthModal(true);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                                >
                                    Log In
                                </button>
                                <button
                                    onClick={() => {
                                        setAuthView('register');
                                        setShowAuthModal(true);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
                                >
                                    Sign Up
                                </button>
                            </div>
                        )}

                        {/* User Info (when logged in) */}
                        {currentUser && (
                            <div className="flex items-center space-x-2 text-sm">
                                <div className="w-8 h-8 bg-blue-600 dark:bg-blue-500 rounded-full flex items-center justify-center">
                                    <span className="text-white font-medium">
                                        {currentUser.displayName
                                            ? currentUser.displayName.charAt(0).toUpperCase()
                                            : currentUser.email.charAt(0).toUpperCase()
                                        }
                                    </span>
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            {currentUser.displayName || 'User'}
                                        </p>
                                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${userTier === 'founder'
                                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                                : userTier === 'paid'
                                                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                            }`}>
                                            {userTier === 'founder' ? '⭐ FOUNDER' : userTier === 'paid' ? 'PRO' : 'FREE'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{currentUser.email}</p>
                                </div>
                            </div>
                        )}

                        {/* Settings Menu (only show when logged in) */}
                        {currentUser && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowMenu(!showMenu)}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    aria-label="Settings menu"
                                >
                                    <svg
                                        className="w-6 h-6 text-gray-600 dark:text-gray-300"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                        />
                                    </svg>
                                    {showMenu && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                                            {/* <button
                                                onClick={handleExport}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                <span>Export Tasks</span>
                                            </button> */}

                                            <button
                                                onClick={handleImport}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                <span>Import Tasks</span>
                                            </button>

                                            <button
                                                onClick={handleDownloadTemplate}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span>Download Template</span>
                                            </button>

                                            <hr className="my-1 border-gray-200 dark:border-gray-700" />

                                            <button
                                                onClick={handleViewStats}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                                <span>View Statistics</span>
                                            </button>

                                            <button
                                                onClick={handleLogout}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                </svg>
                                                <span>Sign Out</span>
                                            </button>
                                            <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 text-center">
                                                v{version}
                                            </div>
                                        </div>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu - Hamburger Button */}
                    <div className="flex md:hidden items-center space-x-2">
                        {/* Notification Bell on mobile (when logged in) */}
                        {currentUser && <NotificationBell />}

                        <button
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            aria-label="Menu"
                        >
                            <svg
                                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                {showMobileMenu ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Dropdown */}
                {showMobileMenu && (
                    <div className="md:hidden mt-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                        {currentUser ? (
                            <>
                                {/* User Info */}
                                <div className="flex items-center space-x-3 mb-4 px-2">
                                    <div className="w-10 h-10 bg-blue-600 dark:bg-blue-500 rounded-full flex items-center justify-center">
                                        <span className="text-white font-medium text-lg">
                                            {currentUser.displayName
                                                ? currentUser.displayName.charAt(0).toUpperCase()
                                                : currentUser.email.charAt(0).toUpperCase()
                                            }
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium text-gray-900 dark:text-white truncate">
                                                {currentUser.displayName || 'User'}
                                            </p>
                                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${userTier === 'paid'
                                                ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
                                                : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                                }`}>
                                                {userTier === 'paid' ? 'PRO' : 'FREE'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{currentUser.email}</p>
                                    </div>
                                </div>

                                {/* Calendar Selector */}
                                <div className="mb-3 px-2">
                                    <CalendarSelector onManageClick={() => { setShowCalendarManager(true); setShowMobileMenu(false); }} />
                                </div>

                                {/* Dark Mode Toggle */}
                                <button
                                    onClick={() => { toggleTheme(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg touch-manipulation"
                                >
                                    {isDark ? (
                                        <>
                                            <svg className="w-5 h-5 flex-shrink-0 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                                            </svg>
                                            <span>Light Mode</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5 flex-shrink-0 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                                            </svg>
                                            <span>Dark Mode</span>
                                        </>
                                    )}
                                </button>

                                <div className="my-3 border-t border-gray-200 dark:border-gray-700"></div>

                                {/* Menu Items */}
                                {/* <button
                                    onClick={() => { handleExport(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg touch-manipulation"
                                >
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    <span>Export Tasks</span>
                                </button> */}

                                <button
                                    onClick={() => { handleImport(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg touch-manipulation"
                                >
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    <span>Import Tasks</span>
                                </button>

                                <button
                                    onClick={() => { handleDownloadTemplate(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg touch-manipulation"
                                >
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span>Download Template</span>
                                </button>

                                <button
                                    onClick={() => { handleViewStats(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg touch-manipulation"
                                >
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span>View Statistics</span>
                                </button>

                                <div className="my-3 border-t border-gray-200 dark:border-gray-700"></div>

                                <button
                                    onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg touch-manipulation"
                                >
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    <span>Sign Out</span>
                                </button>
                                <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
                                    v{version}
                                </p>
                            </>
                        ) : (
                            <>
                                {/* Dark Mode Toggle for logged out users */}
                                <button
                                    onClick={() => { toggleTheme(); setShowMobileMenu(false); }}
                                    className="w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 flex items-center space-x-3 rounded-lg mb-3 touch-manipulation"
                                >
                                    {isDark ? (
                                        <>
                                            <svg className="w-5 h-5 flex-shrink-0 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                                            </svg>
                                            <span>Light Mode</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5 flex-shrink-0 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                                            </svg>
                                            <span>Dark Mode</span>
                                        </>
                                    )}
                                </button>

                                <button
                                    onClick={() => {
                                        setAuthView('login');
                                        setShowAuthModal(true);
                                        setShowMobileMenu(false);
                                    }}
                                    className="w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 active:bg-gray-200 dark:hover:bg-gray-700 dark:active:bg-gray-600 rounded-lg transition-colors touch-manipulation"
                                >
                                    Log In
                                </button>
                                <button
                                    onClick={() => {
                                        setAuthView('register');
                                        setShowAuthModal(true);
                                        setShowMobileMenu(false);
                                    }}
                                    className="w-full mt-2 px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 dark:active:bg-blue-700 transition-colors touch-manipulation"
                                >
                                    Sign Up
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Click outside to close menu */}
            {showMenu && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMenu(false)}
                />
            )}

            {/* Auth Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-screen overflow-y-auto">
                        {/* Close button */}
                        <button
                            onClick={() => setShowAuthModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Auth content */}
                        <div className="p-6">
                            {authView === 'login' ? (
                                <Login
                                    onSwitchToRegister={() => setAuthView('register')}
                                    onSuccess={() => setShowAuthModal(false)}
                                />
                            ) : (
                                <Register
                                    onSwitchToLogin={() => setAuthView('login')}
                                    onSuccess={() => setShowAuthModal(false)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Calendar Manager Modal */}
            <CalendarManager
                isOpen={showCalendarManager}
                onClose={() => setShowCalendarManager(false)}
            />
        </header>
    );
};

export default Header;